/**
 * Het rechtenmodel. Bewust een tabel in code en geen rij in de database:
 * plan §6.3 rekent dit tot de dingen die niet instelbaar mogen zijn, omdat
 * "een Admin had het uit kunnen zetten" betekent dat het nooit een controle
 * was. Een Admin zet iemands vinkjes; wat een vinkje oplevert verandert
 * alleen met een deploy.
 *
 * Er zijn drie lijsten en een rest. `IEDEREEN_MAG` is wat je met een inlog
 * sowieso mag: kijken, en je eigen gesprekken. `BUDDY_MAG` is wat het buddyvinkje oplevert.
 * `CORE_TEAM_MAG` is wat het Core Team deelt met de Admin. Alles wat in
 * geen van drieën staat is van de Admin alleen. Zo blijft de eigenschap
 * overeind waar het eerder om ging: komt er een recht bij, dan landt het
 * automatisch bij de Admin en slaat het hem niet stilletjes over.
 *
 * De eerste lijst is er bijgekomen omdat het model geen "allebei" kende. Elk
 * recht hoorde bij precies één vinkje, en het dashboard naar de buddy verplaatsen
 * zou het de Admin afpakken. Kijken is nu geen vinkje maar de bodem. De
 * derde lijst heeft dezelfde vorm, een trede hoger: het beheer van de personen
 * hoort bij twee vinkjes en niet bij één.
 *
 * Let op wat "van de Admin" NIET betekent. Het is de verzameling
 * handelingen, niet de verzameling gegevens: `meeting:read:own` blijft
 * rij-gebonden, en die rijgrens wordt in `meeting.repo.ts` afgedwongen met een
 * verplichte `buddyId` in de WHERE. Wie zowel buddy als Admin is komt dus
 * bij zijn éigen gesprekken en bij niemands andere. Dat is de grens uit §2.5 en
 * die schuift hier niet.
 */
export const RECHTEN = [
    /** Gesprek uploaden en de eigen verwerking doorlopen. */
    'meeting:create',
    /** Eigen gesprekken lezen. Rij-niveau wordt in de querylaag afgedwongen: elke
     *  functie in `meeting.repo.ts` neemt `buddyId` verplicht mee in de WHERE. */
    'meeting:read:own',
    'meeting:delete:own',
    /** Transcript lezen en goedkeuren. */
    'transcript:read:own',
    'transcript:confirm:own',
    /** Hulpvragen van een eigen gesprek vaststellen. */
    'keyword:review:own',
    /** Geaggregeerde cijfers; nooit herleidbaar tot een persoon. */
    'dashboard:read',
    /** De personenlijst en het begeleidingsbord inzien. Wat je van een rij te
     *  zien krijgt hangt af van of je hem mag wijzigen; dat is een rijgrens en
     *  die staat in `personen.repo.ts`. */
    'persoon:read',
    /** De hulpvragenlijst en het bewijs eronder inzien. Bijstellen is
     *  `vocabulary:manage`. */
    'taxonomie:read',
    'vocabulary:manage',
    /** Toegang: wie mag inloggen, en de uitnodigingen. */
    'account:manage',
    /** Alleen de eigen gecoachten: aanmaken, koppelen, hun opdracht zetten. */
    'begeleiding:manage:own',
    /** Personen, niveaus, opdrachten en de begeleiding. */
    'persoon:manage',
    'settings:read',
    'settings:write',
];
/**
 * Wat je met een inlog sowieso mag: kijken, en verder niets.
 *
 * Een buddy begeleidt mensen, dus hij hoort te zien waar de club tegenaan loopt
 * en hoe de begeleiding ervoor staat. Wijzigen staat daar los van: dat gaat over
 * zijn eigen rijen, net als bij zijn gesprekken.
 *
 * Dit is ook precies wat het View Only-vinkje oplevert, en niets meer: dat vinkje
 * staat in geen van de lijsten hieronder en geeft alleen de inlog.
 *
 * `meeting:read:own` hoort hier omdat het scherm Mijn gesprekken van iedereen
 * is. Het valt niet uit de toon: de rijgrens in `meeting.repo.ts` doet het werk,
 * dus wie geen gesprekken heeft aangeleverd krijgt een lege lijst. Aanleveren
 * blijft bij `meeting:create` en dus bij het buddyvinkje.
 *
 * Alle vier zijn ze leesrechten op iets dat niet tot één persoon te herleiden
 * is, of waar de rijgrens het werk doet. Zet hier niets in dat schrijft.
 */
const IEDEREEN_MAG = [
    'dashboard:read',
    'persoon:read',
    'taxonomie:read',
    'meeting:read:own',
];
/**
 * Wat het buddyvinkje oplevert: zijn eigen gesprekken, van uploaden tot
 * vaststellen, en zijn eigen gecoachten.
 *
 * De rest van `RECHTEN`, op de lijsten hierboven en hieronder na, hoort bij het
 * Admin-vinkje. Een Admin zonder buddyvinkje mist dus `meeting:create` en de
 * `:own` rechten, en dat is de bedoeling: hij levert geen gesprekken aan. Het
 * scherm Mijn gesprekken ziet hij wel, want lezen zit in `IEDEREEN_MAG`; die
 * lijst is dan leeg. Hij houdt `persoon:manage` en beheert daarmee de
 * begeleiding van iedereen, ook zonder `begeleiding:manage:own`.
 */
const BUDDY_MAG = [
    'meeting:create',
    'meeting:delete:own',
    'transcript:read:own',
    'transcript:confirm:own',
    'keyword:review:own',
    'begeleiding:manage:own',
];
/**
 * Wat het Core Team-vinkje oplevert, en wat de Admin daarmee deelt: de
 * personen en de begeleiding van iedereen, plus de uitnodigingen die daarbij
 * horen.
 *
 * Dit is de enige lijst die naar twee vinkjes kijkt. Dat moet ook: het beheer
 * van de personen naar het Core Team verplaatsen zou het de Admin afpakken,
 * en dat is dezelfde klem waar `IEDEREEN_MAG` uit is ontstaan.
 *
 * `account:manage` hoort erbij omdat uitnodigen zelf onder `persoon:manage`
 * valt. Zonder dat recht verstuurt een Core Team-lid een uitnodiging die hij
 * daarna niet ziet staan.
 *
 * Wat hier bewust niet in staat: `vocabulary:manage` en de twee
 * `settings:`-rechten. Dat is precies het verschil met de Admin.
 */
const CORE_TEAM_MAG = ['persoon:manage', 'account:manage'];
/**
 * De rechten met een leesbaar label, in de volgorde waarin ze iets vertellen:
 * eerst wat iemand met gesprekken kan, dan wat hij van de organisatie ziet, dan
 * wat hij mag veranderen.
 *
 * Hier stonden ook `theme:manage`, `export:create` en `audit:read`. Die zijn
 * eruit: er was geen route en geen scherm dat ze gebruikte, dus de tabel
 * beloofde drie dingen die de app niet doet. Komt de functie er, dan komt het
 * recht in datzelfde ticket mee.
 *
 * Bestaat zodat het personenscherm een matrix kan opbouwen uit de tabel hierboven
 * in plaats van uit een tweede, handgeschreven lijst. Dat het compleet blijft
 * staat in `rechten.test.ts`: een rechtenoverzicht dat uit de pas loopt met de
 * werkelijke rechten is erger dan geen overzicht.
 */
export const RECHTEN_OVERZICHT = [
    { recht: 'meeting:create', label: 'Gesprekken uploaden' },
    { recht: 'meeting:read:own', label: 'Eigen gesprekken lezen' },
    { recht: 'meeting:delete:own', label: 'Eigen gesprekken verwijderen' },
    { recht: 'transcript:read:own', label: 'Eigen transcripten lezen' },
    { recht: 'transcript:confirm:own', label: 'Transcript goedkeuren' },
    { recht: 'keyword:review:own', label: 'Hulpvragen vaststellen' },
    { recht: 'dashboard:read', label: 'Geaggregeerde cijfers' },
    { recht: 'persoon:read', label: 'Personen en begeleiding inzien' },
    { recht: 'taxonomie:read', label: 'Hulpvragenlijst inzien' },
    { recht: 'vocabulary:manage', label: 'Vaktermen en onderwerpen' },
    { recht: 'account:manage', label: 'Toegang en uitnodigingen' },
    { recht: 'begeleiding:manage:own', label: 'Eigen gecoachten en hun opdracht' },
    { recht: 'persoon:manage', label: 'Personen en begeleiding' },
    { recht: 'settings:read', label: 'Instellingen lezen' },
    { recht: 'settings:write', label: 'Instellingen wijzigen' },
];
/**
 * Mag deze persoon dit?
 *
 * Eerst de bodem: kijken mag iedereen die kan inloggen. Daarna één tak per
 * lijst, van smal naar breed, en de laatste regel is de rest. Wie meerdere
 * vinkjes heeft mag alles uit die takken bij elkaar, zonder dat "alles" ergens
 * is opgeschreven.
 */
export function mag(rechten, recht) {
    if (!rechten)
        return false;
    if (IEDEREEN_MAG.includes(recht))
        return kanInloggen(rechten);
    if (BUDDY_MAG.includes(recht))
        return rechten.buddy;
    if (CORE_TEAM_MAG.includes(recht))
        return rechten.coreTeam || rechten.admin;
    return rechten.admin;
}
/**
 * Kan deze persoon überhaupt inloggen?
 *
 * Zonder vinkje komt iemand in gesprekken voor en staat hij in de
 * administratie, en dat is alles. Het View Only-vinkje bestaat alleen voor deze
 * vraag: het opent de deur en geeft daarachter niets extra's.
 *
 * Wie zijn vinkjes verliest houdt zijn inlogrij (en dus zijn wachtwoord), maar
 * komt er niet meer in tot hij er een terugkrijgt.
 */
export function kanInloggen(rechten) {
    return Boolean(rechten && (rechten.buddy || rechten.coreTeam || rechten.admin || rechten.viewOnly));
}
